import { ButtonProps } from "@/components/Button";
import { ButtonWithSigninDialogFallback } from "@/components/Modal/ButtonWithSigninDialogFallback";
import { ConfirmDialog } from "@/components/Modal/ConfirmDialog";
import { LearningOpportunity } from "@/server/backend-api";
import {
  LearningOpportunityStatus,
  LearningOpportunityType,
  PrerequisiteResultWithEloId,
} from "@/server/backend-api/generated";
import { mcn } from "@/utils";
import { format } from "date-fns";
import { ReactNode, useMemo, useState } from "react";
import IconWarning from "./Icons/IconWarning";

type Props = {
  learningOpportunity: LearningOpportunity;
  onSimplifiedProcessWithoutDocumentsJoinCallback?: () => void;
  isSticking: boolean;
  prereq?: PrerequisiteResultWithEloId;
};

type PrereqFailureItem = {
  reason: string;
  isMandatory: boolean;
};

const supportedTagRegex = /<(a|b)(\s+href="([^"]+)")?>(.*?)<\/\1>/gi;

const renderReasonWithFormatting = (reason: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = supportedTagRegex.exec(reason)) !== null) {
    const [fullMatch, tagName, , href, content] = match;
    const matchStart = match.index;
    const matchEnd = matchStart + fullMatch.length;

    if (matchStart > lastIndex) {
      nodes.push(
        <span key={`text-${key++}`}>{reason.slice(lastIndex, matchStart)}</span>
      );
    }

    if (tagName.toLowerCase() === "a") {
      if (href) {
        nodes.push(
          <a
            key={`link-${key++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary hover:no-underline"
          >
            {content}
          </a>
        );
      } else {
        nodes.push(<span key={`text-${key++}`}>{content}</span>);
      }
    } else if (tagName.toLowerCase() === "b") {
      nodes.push(<strong key={`bold-${key++}`}>{content}</strong>);
    }

    lastIndex = matchEnd;
  }

  if (lastIndex < reason.length) {
    nodes.push(<span key={`text-${key++}`}>{reason.slice(lastIndex)}</span>);
  }

  return nodes;
};

export const LearningOpportunityApplyButton = ({
  learningOpportunity: learningOpp,
  onSimplifiedProcessWithoutDocumentsJoinCallback,
  isSticking,
  prereq,
}: Props) => {
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showPrereqPopup, setShowPrereqPopup] = useState(false);

  const prerequisiteResult = prereq?.prerequisiteResult ?? [];
  const hasPrereq = prerequisiteResult.length > 0;
  const { disableApplyBecausePrereq, mandatoryFailures, recommendedFailures } =
    useMemo(() => {
      let disableApply = false;
      const mandatory: string[] = [];
      const recommended: string[] = [];

      for (const p of prerequisiteResult) {
        if (!p.isPrerequisiteMet) {
          if (p.isPrerequisiteMandatory) disableApply = true;

          const target = p.isPrerequisiteMandatory ? mandatory : recommended;
          for (const reason of p.reasonsForFailure ?? []) {
            if (reason?.trim()) target.push(reason);
          }
        }
      }

      return {
        disableApplyBecausePrereq: disableApply,
        mandatoryFailures: mandatory,
        recommendedFailures: recommended,
      };
    }, [prerequisiteResult]);

  const hasFailureReasons =
    mandatoryFailures.length > 0 || recommendedFailures.length > 0;

  const prereqDescriptionNode = useMemo(() => {
    if (!hasFailureReasons) return "No prerequisite warnings.";

    return (
      <div className="space-y-4">
        {mandatoryFailures.length > 0 && (
          <div className="space-y-2">
            <p className="font-semibold">Mandatory</p>
            {mandatoryFailures.map((reason, idx) => (
              <p key={`mandatory-${idx}`} className="whitespace-pre-wrap">
                {renderReasonWithFormatting(reason)}
              </p>
            ))}
          </div>
        )}
        {recommendedFailures.length > 0 && (
          <div className="space-y-2">
            <p className="font-semibold">Recommended</p>
            {recommendedFailures.map((reason, idx) => (
              <p key={`recommended-${idx}`} className="whitespace-pre-wrap">
                {renderReasonWithFormatting(reason)}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }, [mandatoryFailures, recommendedFailures, hasFailureReasons]);

  const canApplyBase =
    learningOpp.status === LearningOpportunityStatus.OpenForApplication &&
    !learningOpp.simplifiedAdmissionProcessAndIsFull &&
    !learningOpp.userAdmissionData;

  if (!canApplyBase) return null;

  const canApply = canApplyBase && !disableApplyBecausePrereq;

  const canApplyAndIsSimplifiedWithoutDocuments =
    canApply && learningOpp.simplifiedAdmissionProcessWithNoDocuments === true;

  const learningOppTypeName =
    learningOpp.type === LearningOpportunityType.Challenge
      ? "challenge"
      : "module";

  const endDate = new Date(learningOpp.applicationPeriodEndDate ?? "");

  const sharedButtonProps: ButtonProps = canApply
    ? canApplyAndIsSimplifiedWithoutDocuments
      ? {
          kind: "secondary",
          label: "Join",
          onClick: () => setShowJoinDialog(true),
        }
      : {
          kind: "secondary",
          label: "Apply now - deadline " + format(endDate, "MMMM dd"),
          href: `${learningOpp.url}/apply`,
        }
    : {
        kind: "secondary",
        label: "Apply now - deadline " + format(endDate, "MMMM dd"),
        onClick: () => undefined,
        disabled: true,
      };

  return (
    <div>
      <div className="inline-flex items-center">
        <ButtonWithSigninDialogFallback
          buttonProps={{
            ...sharedButtonProps,
            size: "small",
            className: "hidden laptop:inline-flex",
          }}
        />

        {hasPrereq && hasFailureReasons && (
          <button
            type="button"
            className="ml-2 hidden laptop:inline-flex items-center z-[80]"
            aria-label="Show prerequisite warnings"
            onClick={() => setShowPrereqPopup(true)}
          >
            <IconWarning className="h-6 w-6" />
          </button>
        )}
      </div>

      <div
        className={mcn(
          "fixed bottom-md right-md z-[90] flex items-center gap-2 laptop:hidden",
          !isSticking && "hidden"
        )}
      >
        {hasPrereq && hasFailureReasons && (
          <button
            type="button"
            className="inline-flex items-center"
            aria-label="Show prerequisite warnings"
            onClick={() => setShowPrereqPopup(true)}
          >
            <IconWarning className="h-6 w-6" />
          </button>
        )}

        <ButtonWithSigninDialogFallback
          buttonProps={{
            ...sharedButtonProps,
            className: "shadow-elevation-2",
          }}
        />
      </div>

      <ConfirmDialog
        open={hasPrereq && showPrereqPopup}
        onClose={() => setShowPrereqPopup(false)}
        removeCancel={true}
        title="Prerequisite information"
        description={prereqDescriptionNode}
        confirmActionText="Close"
        confirmType="click"
        onConfirmClick={() => setShowPrereqPopup(false)}
      />

      <ConfirmDialog
        open={showJoinDialog}
        onClose={() => setShowJoinDialog(false)}
        onCancelClick={() => setShowJoinDialog(false)}
        title={`Join ${learningOppTypeName}`}
        description={`By clicking Join you will join this ${learningOppTypeName}.`}
        confirmActionText="Join"
        confirmType="click"
        onConfirmClick={() => {
          if (onSimplifiedProcessWithoutDocumentsJoinCallback) {
            onSimplifiedProcessWithoutDocumentsJoinCallback();
          }
          setShowJoinDialog(false);
        }}
      />
    </div>
  );
};
